import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Building2, 
  Search, 
  Eye, 
  Power, 
  PowerOff,
  RefreshCw,
  UserCheck,
  AlertCircle,
  CreditCard,
  Users
} from 'lucide-react';
import ColumnFilter from '../../components/shared/ColumnFilter';
import ConfirmModal from '../../components/modals/ConfirmModal';
import { getApiErrorMessage } from '../../utils/apiErrors';
import { toast } from 'sonner';
import api from '../../services/api';

/**
 * Businesses Page - Premium Design
 * Matches the Owner module's page styling exactly
 */
const Businesses = () => {
  const [businesses, setBusinesses] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [confirmModal, setConfirmModal] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [columnFilters, setColumnFilters] = useState({
    name: '',
    owner: '',
    plan: '',
    status: []
  });

  useEffect(() => {
    fetchBusinesses();
  }, [filterStatus]);

  const fetchBusinesses = async () => {
    try {
      setLoading(true);

      const params = { limit: 100 };
      if (filterStatus !== 'all') {
        params.status = filterStatus;
      }

      const { data } = await api.get('/admin/businesses', { params });
      setBusinesses(data.businesses || []);
      setTotalCount(data.total || 0);
    } catch (error) {
      console.error('Error fetching businesses:', error);
      toast.error('Failed to load businesses');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = (businessId, newStatus) => {
    const isActivating = newStatus === 'active';
    setConfirmModal({
      title: isActivating ? 'Activate Business' : 'Suspend Business',
      description: isActivating
        ? 'This will restore full access for the business owner and all staff immediately.'
        : 'This will immediately block all access for the business owner and staff.',
      confirmLabel: isActivating ? 'Activate' : 'Suspend',
      confirmClass: isActivating ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700',
      onConfirm: async () => {
        try {
          await api.patch(`/admin/businesses/${businessId}/status`, null, { params: { status: newStatus } });
          toast.success(`Business ${isActivating ? 'activated' : 'suspended'} successfully`);
          fetchBusinesses();
        } catch (error) {
          toast.error(getApiErrorMessage(error, `Failed to ${isActivating ? 'activate' : 'suspend'} business`));
        }
      }
    });
  };

  // Apply filters
  const filteredBusinesses = businesses.filter(business => {
    // Global search
    const matchesSearch = !searchTerm || 
      business.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      business.owner_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      business.email?.toLowerCase().includes(searchTerm.toLowerCase());

    // Column filters
    const matchesName = !columnFilters.name || 
      business.name?.toLowerCase().includes(columnFilters.name.toLowerCase());
    
    const matchesOwner = !columnFilters.owner || 
      business.owner_name?.toLowerCase().includes(columnFilters.owner.toLowerCase());
    
    const matchesPlan = !columnFilters.plan || 
      business.plan_name?.toLowerCase().includes(columnFilters.plan.toLowerCase());
    
    const matchesStatus = columnFilters.status.length === 0 || 
      columnFilters.status.includes(business.status);

    return matchesSearch && matchesName && matchesOwner && matchesPlan && matchesStatus;
  });

  // Calculate stats
  const stats = {
    total: totalCount,
    active: businesses.filter(b => b.status === 'active').length,
    suspended: businesses.filter(b => b.status === 'suspended' || b.status === 'inactive').length
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: { bg: 'bg-emerald-100', text: 'text-emerald-700' },
      inactive: { bg: 'bg-slate-100', text: 'text-slate-600' },
      suspended: { bg: 'bg-rose-100', text: 'text-rose-700' },
      trial: { bg: 'bg-amber-100', text: 'text-amber-700' }
    };
    return badges[status] || badges.inactive;
  };

  // Loading state
  if (loading) {
    return (
      <div className="h-full flex flex-col bg-white" data-testid="businesses-page">
        <div className="flex-shrink-0 bg-white border-b border-slate-200 shadow-sm">
          <div className="px-6 py-4">
            <h1 className="text-[18px] font-semibold text-slate-800">All Businesses</h1>
            <p className="text-[12px] text-slate-500 mt-0.5">Loading...</p>
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center bg-[#F8F9FB]">
          <div className="flex flex-col items-center gap-3">
            <div className="w-10 h-10 border-2 border-rose-200 border-t-rose-500 rounded-full animate-spin" />
            <p className="text-slate-500 text-sm font-light">Loading businesses...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-white" data-testid="businesses-page">
      {/* ═══════════════════════════════════════════════════════════════════════
          PREMIUM HEADER
          ═══════════════════════════════════════════════════════════════════════ */}
      <div className="flex-shrink-0 bg-white border-b border-slate-200 shadow-sm">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-[18px] font-semibold text-slate-800">All Businesses</h1>
              <p className="text-[12px] text-slate-500 mt-0.5">Manage registered businesses</p>
            </div>
            <button
              onClick={fetchBusinesses}
              className="flex items-center gap-2 px-4 py-2 bg-rose-600 text-white rounded-lg text-[13px] font-medium hover:bg-rose-700 transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════════
          STATS PILLS + FILTERS
          ═══════════════════════════════════════════════════════════════════════ */}
      <div className="flex-shrink-0 bg-[#F8F9FB] px-6 py-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          {/* Stats Pills */}
          <div className="flex flex-wrap items-center gap-3">
            {[
              { icon: Building2, label: 'Total', value: stats.total, color: 'slate' },
              { icon: UserCheck, label: 'Active', value: stats.active, color: 'emerald' },
              { icon: AlertCircle, label: 'Suspended', value: stats.suspended, color: 'rose' },
            ].map((stat, idx) => {
              const colorMap = {
                slate: { bg: 'bg-slate-50', icon: 'text-slate-500', value: 'text-slate-600' },
                emerald: { bg: 'bg-emerald-50', icon: 'text-emerald-500', value: 'text-emerald-600' },
                rose: { bg: 'bg-rose-50', icon: 'text-rose-500', value: 'text-rose-600' },
              };
              const colors = colorMap[stat.color];
              
              return (
                <div key={idx} className="inline-flex items-center gap-3 px-4 py-2.5 bg-white rounded-xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow duration-200">
                  <div className={`w-8 h-8 rounded-lg ${colors.bg} flex items-center justify-center`}>
                    <stat.icon className={`w-4 h-4 ${colors.icon}`} />
                  </div>
                  <div>
                    <p className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">{stat.label}</p>
                    <p className={`text-[16px] font-bold ${colors.value}`}>{stat.value}</p>
                  </div>
                </div>
              );
            })}
          </div>
          
          {/* Filters */}
          <div className="flex items-center gap-3">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search businesses..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 pr-4 py-2 border border-slate-200 rounded-lg text-[13px] w-56 focus:outline-none focus:ring-2 focus:ring-rose-200 focus:border-rose-400 bg-white"
              />
            </div>
            {/* Status Filter */}
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:ring-2 focus:ring-rose-200 focus:border-rose-400 bg-white"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
            </select>
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════════
          TABLE AREA
          ═══════════════════════════════════════════════════════════════════════ */}
      <div className="flex-1 overflow-hidden flex flex-col px-4 pb-4 pt-2 bg-[#F8F9FB]">
        <div className="flex-1 flex flex-col bg-white rounded-xl border border-slate-200/60 shadow-sm overflow-hidden">
          {filteredBusinesses.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center">
              <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center mb-4">
                <Building2 className="w-7 h-7 text-slate-400" />
              </div>
              <p className="text-[15px] font-medium text-slate-600">
                {searchTerm ? 'No businesses found' : 'No businesses registered yet'}
              </p>
              <p className="text-[13px] text-slate-400 mt-1">
                {searchTerm ? 'Try adjusting your search' : 'Businesses will appear here when registered'}
              </p>
            </div>
          ) : (
            <div className="flex-1 flex flex-col overflow-hidden">
              {/* Table Header */}
              <div className="flex-shrink-0 overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-100">
                      <th className="px-5 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider bg-gradient-to-r from-rose-50 to-red-50" style={{width: '22%'}}>
                        <div className="flex items-center gap-1">
                          Business Name
                          <ColumnFilter 
                            type="text" 
                            value={columnFilters.name}
                            onChange={(val) => setColumnFilters({...columnFilters, name: val})}
                            placeholder="Filter..."
                          />
                        </div>
                      </th>
                      <th className="px-5 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider bg-gradient-to-r from-rose-50 to-red-50" style={{width: '16%'}}>
                        <div className="flex items-center gap-1">
                          <Users className="w-3.5 h-3.5" />
                          Owner
                          <ColumnFilter 
                            type="text" 
                            value={columnFilters.owner}
                            onChange={(val) => setColumnFilters({...columnFilters, owner: val})}
                            placeholder="Filter..."
                          />
                        </div>
                      </th>
                      <th className="px-5 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider bg-gradient-to-r from-rose-50 to-red-50" style={{width: '14%'}}>
                        <div className="flex items-center gap-1">
                          <CreditCard className="w-3.5 h-3.5" />
                          Plan
                        </div>
                      </th>
                      <th className="px-5 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider bg-gradient-to-r from-rose-50 to-red-50" style={{width: '10%'}}>
                        <div className="flex items-center gap-1">
                          Status
                          <ColumnFilter 
                            type="status" 
                            value={columnFilters.status}
                            onChange={(val) => setColumnFilters({...columnFilters, status: val})}
                            options={[
                              { value: 'active', label: 'Active' },
                              { value: 'inactive', label: 'Inactive' },
                              { value: 'suspended', label: 'Suspended' }
                            ]}
                          />
                        </div>
                      </th>
                      <th className="px-5 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider bg-gradient-to-r from-rose-50 to-red-50" style={{width: '12%'}}>
                        Created
                      </th>
                      <th className="px-5 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider bg-gradient-to-r from-rose-50 to-red-50" style={{width: '14%'}}>
                        Actions
                      </th>
                    </tr>
                  </thead>
                </table>
              </div>

              {/* Table Body */}
              <div className="flex-1 overflow-y-auto">
                <table className="w-full">
                  <tbody className="divide-y divide-slate-100">
                    {filteredBusinesses.map((business, index) => {
                      const statusBadge = getStatusBadge(business.status);
                      return (
                        <tr 
                          key={business.id} 
                          className={`hover:bg-rose-50/30 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-[#FAFBFC]'}`}
                        >
                          <td className="px-5 py-3" style={{width: '22%'}}>
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                <Building2 className="w-4 h-4 text-slate-500" />
                              </div>
                              <div className="min-w-0">
                                <div className="text-[13px] font-medium text-slate-800 truncate">{business.name}</div>
                                {(business.email || business.phone) && (
                                  <div className="text-[11px] text-slate-400 truncate">{business.email || business.phone}</div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-5 py-3 text-[13px] text-slate-600" style={{width: '16%'}}>
                            {business.owner_name || 'N/A'}
                          </td>
                          <td className="px-5 py-3" style={{width: '14%'}}>
                            {business.plan_name ? (
                              <span className={`px-2 py-1 rounded text-[11px] font-medium ${
                                business.plan_name.includes('Field') || business.plan_name.includes('Tracking')
                                  ? 'bg-teal-100 text-teal-700' 
                                  : 'bg-slate-100 text-slate-600'
                              }`}>
                                {business.plan_name}
                              </span>
                            ) : (
                              <span className="text-[11px] text-slate-400">No plan</span>
                            )}
                          </td>
                          <td className="px-5 py-3" style={{width: '10%'}}>
                            <span className={`px-2 py-1 rounded text-[11px] font-medium ${statusBadge.bg} ${statusBadge.text}`}>
                              {business.status || 'unknown'}
                            </span>
                          </td>
                          <td className="px-5 py-3 text-[12px] text-slate-500" style={{width: '12%'}}>
                            {business.created_at ? new Date(business.created_at).toLocaleDateString() : '-'}
                          </td>
                          <td className="px-5 py-3" style={{width: '14%'}}>
                            <div className="flex items-center gap-1">
                              <Link to={`/admin/businesses/${business.id}`}>
                                <button 
                                  className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                                  title="View Details"
                                >
                                  <Eye className="w-4 h-4 text-slate-600" />
                                </button>
                              </Link>
                              {business.status === 'active' ? (
                                <button 
                                  onClick={() => handleStatusChange(business.id, 'suspended')}
                                  className="p-2 hover:bg-rose-50 rounded-lg transition-colors"
                                  title="Suspend"
                                >
                                  <PowerOff className="w-4 h-4 text-rose-600" />
                                </button>
                              ) : (
                                <button 
                                  onClick={() => handleStatusChange(business.id, 'active')}
                                  className="p-2 hover:bg-emerald-50 rounded-lg transition-colors"
                                  title="Activate"
                                >
                                  <Power className="w-4 h-4 text-emerald-600" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Footer */}
              <div className="flex-shrink-0 px-5 py-3 bg-slate-50/80 border-t border-slate-100 flex items-center justify-between text-[12px] text-slate-500">
                <p>
                  Showing <span className="font-medium text-slate-700">{filteredBusinesses.length}</span> of <span className="font-medium text-slate-700">{businesses.length}</span> businesses
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      <ConfirmModal
        isOpen={!!confirmModal}
        title={confirmModal?.title}
        description={confirmModal?.description}
        confirmLabel={confirmModal?.confirmLabel}
        confirmClass={confirmModal?.confirmClass}
        onConfirm={async () => { const fn = confirmModal?.onConfirm; setConfirmModal(null); await fn?.(); }}
        onClose={() => setConfirmModal(null)}
      />
    </div>
  );
};

export default Businesses;
